import { useCallback, useEffect, useRef, useState } from 'react';

import type { Anomalie } from './types';

/**
 * Client HTTP. En production l'API et le SPA sont servis par la même image :
 * les appels sont relatifs (`/api/…`) et il n'y a rien à configurer.
 */

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
    readonly details?: { anomalies?: Anomalie[] } & Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

/** Fournie par la session : jeton Entra, ou identité de développement. */
type FournisseurEnTetes = () => Promise<Record<string, string>>;

let enTetesAuth: FournisseurEnTetes = async () => ({});

export function installeEnTetesAuth(fournisseur: FournisseurEnTetes): void {
  enTetesAuth = fournisseur;
}

export type OptionsAppel = {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corps?: unknown;
  signal?: AbortSignal;
};

export async function appelApi<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  const enTetes: Record<string, string> = { accept: 'application/json', ...(await enTetesAuth()) };
  if (options.corps !== undefined) enTetes['content-type'] = 'application/json';

  const reponse = await fetch(`${BASE}${chemin}`, {
    method: options.methode ?? 'GET',
    headers: enTetes,
    ...(options.corps !== undefined ? { body: JSON.stringify(options.corps) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (reponse.status === 204) return undefined as T;

  const type = reponse.headers.get('content-type') ?? '';

  if (!reponse.ok) {
    if (type.includes('application/json')) {
      const charge = (await reponse.json()) as {
        erreur?: { code?: string; message?: string; details?: Record<string, unknown> };
      };
      throw new ErreurApi(
        reponse.status,
        charge.erreur?.code ?? 'erreur',
        charge.erreur?.message ?? `HTTP ${reponse.status}`,
        charge.erreur?.details ?? undefined,
      );
    }
    throw new ErreurApi(reponse.status, 'erreur', `HTTP ${reponse.status}`);
  }

  if (!type.includes('application/json')) return (await reponse.text()) as T;
  return (await reponse.json()) as T;
}

/** Télécharge un fichier servi par l'API (l'export CSV) en gardant l'auth. */
export async function telechargeFichier(chemin: string, nomPropose: string): Promise<void> {
  const reponse = await fetch(`${BASE}${chemin}`, { headers: await enTetesAuth() });
  if (!reponse.ok) {
    throw new ErreurApi(reponse.status, 'export_refuse', `Export failed (HTTP ${reponse.status})`);
  }

  const blob = await reponse.blob();
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomPropose;
  document.body.append(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}

export type EtatRequete<T> = {
  donnees: T | undefined;
  erreur: ErreurApi | undefined;
  enCours: boolean;
  recharge: () => void;
};

/**
 * Petit hook de lecture. Volontairement minimal : cet outil fait une poignée
 * d'appels par écran, une bibliothèque de cache serait plus lourde que le
 * problème.
 */
export function useRequete<T>(chemin: string | null, dependances: unknown[] = []): EtatRequete<T> {
  const [donnees, setDonnees] = useState<T | undefined>(undefined);
  const [erreur, setErreur] = useState<ErreurApi | undefined>(undefined);
  const [enCours, setEnCours] = useState<boolean>(chemin !== null);
  const [compteur, setCompteur] = useState(0);
  const dernierChemin = useRef<string | null>(null);

  useEffect(() => {
    if (chemin === null) {
      setDonnees(undefined);
      setEnCours(false);
      return;
    }

    const controleur = new AbortController();
    dernierChemin.current = chemin;
    setEnCours(true);
    setErreur(undefined);

    appelApi<T>(chemin, { signal: controleur.signal })
      .then((resultat) => {
        setDonnees(resultat);
        setEnCours(false);
      })
      .catch((cause: unknown) => {
        if (controleur.signal.aborted) return;
        setErreur(
          cause instanceof ErreurApi
            ? cause
            : new ErreurApi(0, 'reseau', cause instanceof Error ? cause.message : 'Request failed'),
        );
        setEnCours(false);
      });

    return () => controleur.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chemin, compteur, ...dependances]);

  const recharge = useCallback(() => setCompteur((c) => c + 1), []);

  return { donnees, erreur, enCours, recharge };
}
